/**
 * S-CP10-4 — RULE-FORECAST-1 wiring
 *
 * Declared timing % governs forecast-category eligibility (the "category
 * floor") and the displayed forecast %:
 *   displayed_forecast_pct = composite_pct × timing_pct
 *
 * Locked framings (do not paraphrase in copy):
 *   "if you're only 50% confident on the timeframe, that's a radical data point"
 *   "even if you have something that scores at the highest levels on everything
 *    else, but potentially low on the timing part something that major should
 *    probably disqualify it from being at least in a commit bucket and put it
 *    in the upside for possible"
 */

import type { DeclaredTimingButton } from "./cp10-timing-band";

// Canon 5-bucket taxonomy — CANON.md lines 167-186.
// Legacy "Push" and "Loss" are both mapped to "Omit" at read-time.
export type ForecastCategory = "Commit" | "Upside" | "Best Case" | "Pipeline" | "Omit";

export interface CategoryEligibility {
  commit_eligible: boolean;
  best_case_eligible: boolean;
  pipeline_eligible: boolean;
  /** True when timing < 50 — deal exits active forecast (maps to Omit). */
  omit_only: boolean;
  manager_attention_flag: boolean;
  notes: string;
}

export interface ForecastDisplay {
  composite_pct: number;
  timing_pct: number;
  blended_forecast_pct: number;
  below_band: boolean;
  below_band_reason: string | null;
  eligibility: CategoryEligibility;
}

const TIMING_PCT_MAP: Record<DeclaredTimingButton, number> = {
  "90": 90,
  "80": 80,
  "70": 70,
  "60": 60,
  "50": 50,
  below_50: 49,
};

export function getCategoryEligibility(button: DeclaredTimingButton): CategoryEligibility {
  switch (button) {
    case "90":
      return {
        commit_eligible: true,
        best_case_eligible: true,
        pipeline_eligible: true,
        omit_only: false,
        manager_attention_flag: false,
        notes: "All categories available.",
      };
    case "80":
    case "70":
      return {
        commit_eligible: false,
        best_case_eligible: true,
        pipeline_eligible: true,
        omit_only: false,
        manager_attention_flag: false,
        notes: "Commit not available. Score-driven max: Upside or Best Case.",
      };
    case "60":
      return {
        commit_eligible: false,
        best_case_eligible: false,
        pipeline_eligible: true,
        omit_only: false,
        manager_attention_flag: false,
        notes: "Commit and Best Case blocked. Pipeline only.",
      };
    case "50":
      return {
        commit_eligible: false,
        best_case_eligible: false,
        pipeline_eligible: true,
        omit_only: false,
        manager_attention_flag: true,
        notes: "Pipeline only. Manager attention flag raised — 50% timing confidence is a significant signal.",
      };
    case "below_50":
      return {
        commit_eligible: false,
        best_case_eligible: false,
        pipeline_eligible: false,
        omit_only: true,
        manager_attention_flag: true,
        // Legacy: "PUSH or LOSS only" — both map to Omit under canon.
        notes: "Not in active pipeline. Deal maps to Omit (was: PUSH or LOSS).",
      };
  }
}

export function computeBlendedForecast(composite_pct: number, button: DeclaredTimingButton): number {
  const timing_pct = TIMING_PCT_MAP[button];
  return Math.round((composite_pct / 100) * (timing_pct / 100) * 100);
}

export function buildForecastDisplay(
  composite_pct: number,
  button: DeclaredTimingButton,
  band_lower: number,
  band_upper: number,
): ForecastDisplay {
  const timing_pct = TIMING_PCT_MAP[button];
  const blended_forecast_pct = computeBlendedForecast(composite_pct, button);
  const eligibility = getCategoryEligibility(button);
  const below_band = timing_pct < band_lower;
  const below_band_reason = below_band
    ? `Your timing is below the system recommendation. Declared: ${
        timing_pct === 49 ? "Below 50" : `${timing_pct}%`
      }. System band: ${band_lower}–${band_upper}%.`
    : null;

  return { composite_pct, timing_pct, blended_forecast_pct, below_band, below_band_reason, eligibility };
}

export function getManagerDashboardDisplay(forecast: ForecastDisplay): string {
  const timingLabel = forecast.timing_pct === 49 ? "<50%" : `${forecast.timing_pct}%`;
  return `Composite: ${forecast.composite_pct}%  |  Timing: ${timingLabel}  |  Blended: ${forecast.blended_forecast_pct}%`;
}
